import { useEffect, useState } from 'react';
import { Store } from 'store';

import { useIntl, FormattedMessage } from 'react-intl';
import { Services } from 'services';
import { ProtocolInfoDto, ReferralDto, ProtocolDto } from 'common/api';
import { Box, RadioButton, Text } from '@clariness/clarikit-react';
import { SponsorID } from './components/SponsorID';
import { Protocol801 } from './components/Protocol801';
import { HealthcareProviderDetails } from './components/HealthcareProviderDetails';
import { MedicalRecords } from './components/MedicalRecords';
import { usePatientDetailsContext } from '../../../../ReferralDetails';

type Props = {
  referral: ReferralDto;
  Protocol801Participation: (value: any) => void;
  refetch: () => void;
};

export const StudyDetails = ({ referral, Protocol801Participation }: Props) => {
  const intl = useIntl();
  const { isExternalReferral } = usePatientDetailsContext();

  const { protocolsData } = Services.Referrals.useGetReferralProtocol(
    isExternalReferral ? '' : referral?.id || '',
    { enabled: !isExternalReferral && Boolean(referral?.id) }
  );

  const [selectedProtocol, setSelectedProtocol] = useState<ProtocolInfoDto>();
  const { openModal, closeModal } = Store.useModal();

  useEffect(() => {
    if (protocolsData?.selectedProtocol) {
      setSelectedProtocol(protocolsData?.selectedProtocol);
    }
  }, [protocolsData]);

  const handleChangeProtocol = (protocol: ProtocolInfoDto) => {
    setSelectedProtocol(protocol);
    closeModal();
    if (referral?.id) {
      openModal({
        modalType: 'ProtocolConfirmation',
        modalProps: {
          referral,
          protocol: { ...protocol },
        },
      });
    }
  };

  const studyTitle = intl.formatMessage({
    id: 'is.patient_details_modal.basic_details.study',
    defaultMessage: 'Study',
  });

  return (
    <Box width="100%">
      <Box display="flex" flexDirection="column" gap="8" width="100%">
        <Text as="h6" variant="headingXs">
          {studyTitle}
        </Text>
        <Box width="100%" height="1px" backgroundColor="color-gray-variant-4" />
        <Box as="dl" display="flex" flexDirection="column" gap="16" margin="0">
          <Box display="flex" flexWrap="wrap" gap="40" width="100%">
            <Box as="dt" style={{ flex: '0 0 150px' }}>
              <Text as="span" variant="bodyMd" fontWeight="bold">
                {studyTitle}
              </Text>
            </Box>
            <Box as="dd" margin="0" minWidth="0" style={{ flex: '1 1 12rem' }}>
              <Text as="span" variant="bodyMd">
                {referral?.study.name}
              </Text>
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap" gap="40" width="100%">
            <Box as="dt" style={{ flex: '0 0 150px' }}>
              <Text as="span" variant="bodyMd" fontWeight="bold">
                {intl.formatMessage({
                  id: 'is.patient_details_modal.basic_details.Site',
                  defaultMessage: 'Site',
                })}
              </Text>
            </Box>
            <Box as="dd" margin="0" minWidth="0" style={{ flex: '1 1 12rem' }}>
              <Text as="span" variant="bodyMd">
                {referral?.site?.name}
              </Text>
            </Box>
          </Box>

          <SponsorID referral={referral} />

          {referral?.study?.trackingId === 1605 && (
            <Box display="flex" flexWrap="wrap" gap="40" width="100%">
              <Box as="dt" style={{ flex: '0 0 150px' }}>
                <Text as="span" variant="bodyMd" fontWeight="bold">
                  {intl.formatMessage({
                    id: 'is.patient_details_modal.basic_details.protocol_801',
                    defaultMessage: 'Protocol 801 if applicable',
                  })}
                </Text>
              </Box>
              <Box
                as="dd"
                margin="0"
                minWidth="0"
                style={{ flex: '1 1 12rem' }}
              >
                <Protocol801
                  referral={referral}
                  Protocol801Participation={Protocol801Participation}
                />
              </Box>
            </Box>
          )}

          {protocolsData?.protocolList?.length !== 0 &&
            protocolsData?.protocolsCount !== 0 && (
              <Box display="flex" flexWrap="wrap" gap="40" width="100%">
                <Box as="dt" style={{ flex: '0 0 150px' }}>
                  <Text as="span" variant="bodyMd" fontWeight="bold">
                    {intl.formatMessage({
                      id: 'is.patient_details_modal.basic_details.available_protocols',
                      defaultMessage: 'Available Protocols',
                    })}
                  </Text>
                </Box>
                <Box
                  as="dd"
                  margin="0"
                  display="flex"
                  flexDirection="column"
                  gap="8"
                  minWidth="0"
                  style={{ flex: '1 1 12rem' }}
                >
                  {protocolsData?.protocolList?.map(
                    (protocol: ProtocolInfoDto) => {
                      const isSelected = protocol.id === selectedProtocol?.id;
                      return (
                        <RadioButton
                          key={protocol.id}
                          id={protocol.id.toString()}
                          labelText={`${protocol.internalId || '...'} - ${protocol.name}`}
                          selected={isSelected}
                          // TODO(clarikit): RadioButton toggles itself off when clicked while selected.
                          onClickCapture={(event) =>
                            isSelected && event.stopPropagation()
                          }
                          handleSelect={(select) =>
                            select && handleChangeProtocol(protocol)
                          }
                        />
                      );
                    }
                  )}
                </Box>
              </Box>
            )}
        </Box>
      </Box>

      {Boolean(
        referral?.patient?.qualifiedStudyProtocols &&
          referral?.patient?.qualifiedStudyProtocols?.length > 0
      ) && (
        <Box
          display="flex"
          flexDirection="column"
          gap="8"
          style={{ marginTop: '40px' }}
        >
          <Text as="p" fontWeight="bold">
            <FormattedMessage
              id="is.qualified.study.protocols.message"
              defaultMessage="Based on screener answers eligible for :"
            />
          </Text>
          <Text as="p">
            {referral?.patient?.qualifiedStudyProtocols
              ?.map((protocol: ProtocolDto) => ` ${protocol.name}`)
              .join(',')}
          </Text>
        </Box>
      )}

      <HealthcareProviderDetails
        healthcareProvider={referral?.healthcareProvider}
      />

      <MedicalRecords
        referralId={referral?.id}
        patientInternalId={referral?.patient?.internalId}
        isExternalReferral={isExternalReferral}
      />
    </Box>
  );
};
